import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearch } from '@tanstack/react-router';
import {
  Badge,
  Box,
  Button,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  UnstyledButton,
} from '@mantine/core';
import { BULK_OPERATIONS, CANONICAL_TARGETS, HREFLANG_ROLES } from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import classes from './EntityPage.module.css';

const OPERATION_LABELS: Record<string, string> = {
  canonical: 'Канонікал',
  hreflang: 'Hreflang',
  relink: 'Переклейка оферу',
  redirects: 'Редиректи',
  add_folder: 'Додати папку',
  regenerate: 'Перегенерація',
  translate: 'Переклад',
  replace_offer: 'Заміна оферу',
  redeploy: 'Передеплой',
};

const RISK_COLOR: Record<string, string> = { low: 'graphite', medium: 'amber', high: 'rose' };

/** Renders the `{ from, to }` map the plan stores per site. */
function diffLines(diff: unknown): { field: string; from: string; to: string }[] {
  if (typeof diff !== 'object' || diff === null) return [];
  return Object.entries(diff as Record<string, { from?: unknown; to?: unknown }>).map(
    ([field, change]) => ({
      field,
      from: JSON.stringify(change.from ?? null),
      to: JSON.stringify(change.to ?? null),
    }),
  );
}

/**
 * Масові операції (§5), acceptance scenario 11.
 *
 * The screen is built around the fact that nothing here is undoable by eye: the operator
 * plans, reads a diff per domain with what will be skipped and why, and only then runs. The
 * run button is disabled until a plan exists, and the rollback button exists next to it,
 * because the moment somebody needs it they will not be reading documentation.
 */
export function BulkPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  /**
   * The scope arrives from the sites registry, in the URL.
   *
   * `useState(initial)` and not a `useEffect` that syncs: the search params are the starting
   * point, not a live source. Once the operator drops a domain from the list here, a re-render
   * must not put it back.
   */
  const search = useSearch({ from: '/authed/bulk' });
  const navigate = useNavigate();

  const [operation, setOperation] = useState<string>(search.operation ?? 'canonical');
  const [selected, setSelected] = useState<string[]>(search.siteIds ?? []);
  const [canonicalTarget, setCanonicalTarget] = useState<string>('drop');
  const [hreflangRole, setHreflangRole] = useState<string>('x-default');
  /**
   * Операція може прийти в адресі — із журналу картки сайту.
   *
   * Панель «Історія операцій» звідси прибрана: мокап її не малює, а точка входу у відкат за
   * планом переїхала в журнал картки. Але «відкотити одну зміну на одному сайті» і «відкотити
   * операцію на сорока доменах» — різні дії, і друга мусила лишитись досяжною. Тому запис
   * журналу, який поставила масова операція, веде сюди з її ідентифікатором.
   */
  const [planId, setPlanId] = useState<string | null>(search.operationId ?? null);
  const [reason, setReason] = useState('');
  const [scopeOpen, setScopeOpen] = useState(false);
  const [sourceSiteId, setSourceSiteId] = useState<string | null>(null);

  /** Candidates for «перекладати з». Loaded only when the operation actually needs one. */
  const sourceOptions = useQuery({
    ...trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
    enabled: operation === 'translate',
  });

  /**
   * Ids → domains, so «ПЕРЕГЛЯНУТИ СПИСОК» shows domains and not uuids.
   *
   * Read through the ordinary registry filter, which means the actor's geo scope narrows it:
   * an id pasted into the URL for a country this operator cannot see resolves to nothing and
   * is reported below rather than silently carried into the plan.
   */
  const scope = useQuery({
    ...trpc.sites.list.queryOptions({
      ids: selected.slice(0, 200),
      pagination: { limit: 200, direction: 'desc' },
    }),
    enabled: selected.length > 0,
  });

  const domainById = new Map((scope.data?.items ?? []).map((site) => [site.id, site.domain]));
  const unresolved = selected.slice(0, 200).filter((id) => !domainById.has(id));
  const plan = useQuery({
    ...trpc.bulk.byId.queryOptions({ id: planId ?? '' }),
    enabled: planId !== null,
  });

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.bulk.pathKey() });
  };

  const planMutation = useMutation(
    trpc.bulk.plan.mutationOptions({
      onSuccess: async (result) => {
        setPlanId(result.id);
        await invalidate();
      },
    }),
  );
  const runMutation = useMutation(trpc.bulk.run.mutationOptions({ onSuccess: invalidate }));
  const rollbackMutation = useMutation(trpc.bulk.rollback.mutationOptions({ onSuccess: invalidate }));
  const requestConfirmation = useMutation(
    trpc.bulk.requestConfirmation.mutationOptions({ onSuccess: invalidate }),
  );
  const confirmMutation = useMutation(trpc.bulk.confirm.mutationOptions({ onSuccess: invalidate }));

  const detail = plan.data;
  const items = detail?.items ?? [];
  const planned = items.filter((item) => item.status === 'planned');
  const resumable = items.some((item) => item.status === 'planned' || item.status === 'running');
  const skipped = items.filter((item) => item.status === 'skipped');

  return (
    <Stack gap="md">
      <Box className={classes.panel} data-testid="bulk-builder">
        <Text className={classes.panelTitle} mb={8}>
          Що робимо
        </Text>
        <Group gap="md" align="flex-end" mb="sm">
          <Select
            label="Операція"
            data={BULK_OPERATIONS.map((value) => ({ value, label: OPERATION_LABELS[value] ?? value }))}
            value={operation}
            onChange={(value) => {
              setOperation(value ?? 'canonical');
              setPlanId(null);
            }}
            data-testid="bulk-operation"
            style={{ width: 220 }}
          />
          {operation === 'canonical' ? (
            <Select
              label="Канонікал на"
              data={[...CANONICAL_TARGETS]}
              value={canonicalTarget}
              onChange={(value) => {
                setCanonicalTarget(value ?? 'drop');
              }}
              data-testid="bulk-canonical-target"
              style={{ width: 160 }}
            />
          ) : null}
          {operation === 'hreflang' ? (
            <Select
              label="Роль hreflang"
              data={[...HREFLANG_ROLES]}
              value={hreflangRole}
              onChange={(value) => {
                setHreflangRole(value ?? 'lang');
              }}
              data-testid="bulk-hreflang-role"
              style={{ width: 160 }}
            />
          ) : null}
          {/*
           * Переклад іде З сайта-джерела: `translatePage` парує сторінки за `page_type`, тож
           * без джерела парувати нема з чим. Без цього поля операція просто відмовлялася б на
           * сухому прогоні — контрол, якого бракує, виглядає як зламана функція.
           */}
          {operation === 'translate' ? (
            <Select
              label="Перекладати з"
              placeholder="сайт-джерело"
              searchable
              data={(sourceOptions.data?.items ?? []).map((site) => ({
                value: site.id,
                label: site.domain,
              }))}
              value={sourceSiteId}
              onChange={(value) => {
                setSourceSiteId(value);
                setPlanId(null);
              }}
              data-testid="bulk-source-site"
              style={{ width: 220 }}
            />
          ) : null}
          <Button
            size="xs"
            disabled={selected.length === 0}
            loading={planMutation.isPending}
            data-testid="bulk-plan"
            onClick={() => {
              planMutation.mutate({
                operation: operation as (typeof BULK_OPERATIONS)[number],
                selection: { siteIds: selected },
                params:
                  operation === 'canonical'
                    ? { canonicalTarget: canonicalTarget as (typeof CANONICAL_TARGETS)[number] }
                    : operation === 'hreflang'
                      ? { hreflangRole: hreflangRole as (typeof HREFLANG_ROLES)[number] }
                      : operation === 'translate' && sourceSiteId !== null
                        ? { sourceSiteId }
                        : {},
              });
            }}
          >
            Сухий прогін
          </Button>
          <Text className={classes.dim} data-testid="bulk-selected-count">
            обрано: <span className={classes.mono}>{selected.length}</span>
          </Text>
        </Group>

        {planMutation.error === null ? null : (
          <Text className={classes.danger} data-testid="bulk-plan-error">
            {publicMessageOf(planMutation.error)}
          </Text>
        )}

        {/*
         * «вибірка · N доменів ▾ · ПЕРЕГЛЯНУТИ СПИСОК» — мокап, рядок 4978.
         *
         * Тут більше немає власного списку сайтів із чекбоксами: він тягнув перші 200 рядків
         * і мовчки обрізав решту, тобто оператор, який хотів переклеїти 5 214 доменів, бачив
         * 200 і не дізнавався про це. Вибірку набирають у реєстрі, де є фільтри; сюди вона
         * приходить у маршруті. Лишається переглянути список і викинути зайве.
         */}
        {selected.length === 0 ? (
          <Group gap={8} data-testid="bulk-no-scope">
            <Text className={classes.dim}>Вибірки немає.</Text>
            <Button
              size="compact-xs"
              variant="default"
              data-testid="bulk-go-registry"
              onClick={() => {
                void navigate({ to: '/sites' });
              }}
            >
              Обрати сайти в реєстрі
            </Button>
          </Group>
        ) : (
          <Stack gap={6} data-testid="bulk-scope">
            <Group gap={8}>
              <Text className={classes.dim}>
                вибірка: <span className={classes.mono}>{selected.length}</span> дом.
              </Text>
              <Button
                size="compact-xs"
                variant="default"
                data-testid="bulk-scope-toggle"
                onClick={() => {
                  setScopeOpen((open) => !open);
                }}
              >
                {scopeOpen ? 'Згорнути список' : 'Переглянути список'}
              </Button>
              {selected.length > 200 ? (
                // Мовчазне обрізання тут коштувало б домену, який оператор бачив у списку
                // і який не потрапив у прогін. Кажемо вголос замість того, щоб показати 200.
                <Text className={classes.warn} data-testid="bulk-scope-truncated">
                  показано перші 200 — решту {selected.length - 200} доведеться прогнати окремо
                </Text>
              ) : null}
              {unresolved.length === 0 ? null : (
                <Text className={classes.danger} data-testid="bulk-scope-unresolved">
                  {unresolved.length} з вибірки недоступні вам за ГЕО і в план не потраплять
                </Text>
              )}
            </Group>

            {!scopeOpen ? null : scope.isPending ? (
              <Loader size="sm" />
            ) : (
              <Box style={{ maxHeight: 220, overflowY: 'auto' }}>
                <Group gap={6} wrap="wrap">
                  {selected.slice(0, 200).map((id) => {
                    const domain = domainById.get(id) ?? id;
                    return (
                      <Badge
                        key={id}
                        size="sm"
                        variant="default"
                        data-testid={`bulk-scope-${domain}`}
                        rightSection={
                          <UnstyledButton
                            aria-label={`Прибрати ${domain} з вибірки`}
                            data-testid={`bulk-scope-drop-${domain}`}
                            onClick={() => {
                              setSelected((current) => current.filter((each) => each !== id));
                              setPlanId(null);
                            }}
                          >
                            ✕
                          </UnstyledButton>
                        }
                      >
                        {domain}
                      </Badge>
                    );
                  })}
                </Group>
              </Box>
            )}
          </Stack>
        )}
      </Box>

      {detail === undefined ? (
        <Box className={classes.panel} data-testid="bulk-no-plan">
          <Text className={classes.dim}>Оберіть домени і зробіть сухий прогін.</Text>
        </Box>
      ) : (
        <>
          <Box className={classes.panel} data-testid="bulk-summary">
            <Group gap="lg" mb={8}>
              <Text className={classes.dim}>
                статус <span className={classes.mono} data-testid="bulk-status">{detail.status}</span>
              </Text>
              <Text className={classes.dim}>
                доменів <span className={classes.mono} data-testid="bulk-total">{detail.sitesTotal}</span>
              </Text>
              <Text className={classes.dim}>
                зміниться <span className={classes.mono} data-testid="bulk-planned">{planned.length}</span>
              </Text>
              <Text className={classes.dim}>
                пропущено <span className={classes.mono} data-testid="bulk-skipped">{skipped.length}</span>
              </Text>
              <Text className={classes.dim}>
                трафік під ризиком{' '}
                <span className={classes.mono} data-testid="bulk-traffic">{detail.trafficAtRisk}</span>
              </Text>
              <Badge size="sm" color={RISK_COLOR[detail.riskLevel] ?? 'graphite'} data-testid="bulk-risk">
                {detail.riskLevel}
              </Badge>
            </Group>

            <Group gap="xs">
              <Button
                size="xs"
                disabled={
                  detail.status !== 'dry_run' &&
                  !(detail.status === 'running' && resumable)
                }
                loading={runMutation.isPending}
                data-testid="bulk-run"
                onClick={() => {
                  runMutation.mutate({ id: detail.id });
                }}
              >
                Застосувати
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={detail.status !== 'done' && detail.status !== 'failed'}
                loading={rollbackMutation.isPending}
                data-testid="bulk-rollback"
                onClick={() => {
                  rollbackMutation.mutate({ id: detail.id });
                }}
              >
                Відкотити
              </Button>
              <TextInput
                size="xs"
                placeholder="причина для підтвердження"
                value={reason}
                onChange={(event) => {
                  setReason(event.currentTarget.value);
                }}
                data-testid="bulk-reason"
                style={{ width: 260 }}
              />
              <Button
                size="xs"
                variant="default"
                loading={requestConfirmation.isPending}
                data-testid="bulk-request-confirmation"
                onClick={() => {
                  requestConfirmation.mutate({ id: detail.id, reason: reason || 'масова операція' });
                }}
              >
                Запросити підтвердження
              </Button>
              <Button
                size="xs"
                variant="default"
                disabled={detail.confirmation === null}
                loading={confirmMutation.isPending}
                data-testid="bulk-confirm"
                onClick={() => {
                  confirmMutation.mutate({ id: detail.id });
                }}
              >
                Підтвердити
              </Button>
            </Group>

            {runMutation.error === null ? null : (
              <Text className={classes.danger} mt={8} data-testid="bulk-run-error">
                {publicMessageOf(runMutation.error)}
              </Text>
            )}
            {confirmMutation.error === null ? null : (
              <Text className={classes.danger} mt={8} data-testid="bulk-confirm-error">
                {publicMessageOf(confirmMutation.error)}
              </Text>
            )}
            {detail.confirmation === null ? null : (
              <Text className={classes.warn} mt={8} data-testid="bulk-confirmation">
                підтверджень: {detail.confirmation.confirmedBy.length} / 2
                {detail.confirmation.satisfied ? ' ✓' : ''}
              </Text>
            )}
          </Box>

          <Box className={classes.panel} data-testid="bulk-diff">
            <Text className={classes.panelTitle} mb={8}>
              Діфф по доменах
            </Text>
            <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Домен</Table.Th>
                  <Table.Th>Стан</Table.Th>
                  <Table.Th>Зміни</Table.Th>
                  <Table.Th>Причина пропуску</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {items.map((item) => (
                  <Table.Tr key={item.id} data-testid={`bulk-item-${item.domain}`}>
                    <Table.Td className={classes.mono}>{item.domain}</Table.Td>
                    <Table.Td>
                      <Badge
                        size="xs"
                        color={
                          item.status === 'applied'
                            ? 'mint'
                            : item.status === 'skipped'
                              ? 'amber'
                              : item.status === 'failed'
                                ? 'rose'
                                : 'graphite'
                        }
                        data-testid={`bulk-item-status-${item.domain}`}
                      >
                        {item.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      {diffLines(item.diff).map((line) => (
                        <Text key={line.field} className={classes.mono} fz="xs">
                          {line.field}: {line.from} → {line.to}
                        </Text>
                      ))}
                    </Table.Td>
                    <Table.Td className={classes.dim} data-testid={`bulk-item-skip-${item.domain}`}>
                      {item.skipReason ?? item.error ?? ''}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>
        </>
      )}

    </Stack>
  );
}
